import React, { useState, useEffect } from "react";
import { cn } from "@/lib/utils";

const ROWS: string[][] = [
  ["`", "1", "2", "3", "4", "5", "6", "7", "8", "9", "0", "-", "="],
  ["q", "w", "e", "r", "t", "y", "u", "i", "o", "p", "[", "]", "\\"],
  ["a", "s", "d", "f", "g", "h", "j", "k", "l", ";", "'"],
  ["z", "x", "c", "v", "b", "n", "m", ",", ".", "/"],
];

// Map shifted characters to their base key
const SHIFT_MAP: Record<string, string> = {
  "!": "1", "@": "2", "#": "3", "$": "4", "%": "5",
  "^": "6", "&": "7", "*": "8", "(": "9", ")": "0",
  "_": "-", "+": "=", "{": "[", "}": "]", "|": "\\",
  ":": ";", "\"": "'", "<": ",", ">": ".", "?": "/",
  "~": "`",
};

// Finger names
const FINGER_NAMES: string[] = [
  "L pinky",   // 0
  "L ring",    // 1
  "L mid",     // 2
  "L index",   // 3
  "R index",   // 4
  "R mid",     // 5
  "R ring",    // 6
  "R pinky",   // 7
];

// Finger colors - background for each finger zone
const FINGER_BG: string[] = [
  "bg-rose-500/15",      // left pinky
  "bg-amber-500/15",     // left ring
  "bg-emerald-500/15",   // left middle
  "bg-sky-500/15",       // left index
  "bg-sky-500/15",       // right index
  "bg-emerald-500/15",   // right middle
  "bg-amber-500/15",     // right ring
  "bg-rose-500/15",      // right pinky
];

const FINGER_BORDER: string[] = [
  "border-b-rose-400",
  "border-b-amber-400",
  "border-b-emerald-400",
  "border-b-sky-400",
  "border-b-sky-400",
  "border-b-emerald-400",
  "border-b-amber-400",
  "border-b-rose-400",
];

const KEY_FINGERS: Record<string, number> = {
  "`":0,"1":0,"2":1,"3":2,"4":3,"5":3,"6":4,"7":4,"8":5,"9":6,"0":7,"-":7,"=":7,
  "q":0,"w":1,"e":2,"r":3,"t":3,"y":4,"u":4,"i":5,"o":6,"p":7,"[":7,"]":7,"\\":7,
  "a":0,"s":1,"d":2,"f":3,"g":3,"h":4,"j":4,"k":5,"l":6,";":7,"'":7,
  "z":0,"x":1,"c":2,"v":3,"b":3,"n":4,"m":4,",":5,".":6,"/":7,
};

interface KeyboardProps {
  lessonKeys: string[];
  activeKey: string;
  pressedCorrect: string | null;
  pressedWrong: string | null;
}

export default function Keyboard({
  lessonKeys,
  activeKey,
  pressedCorrect,
  pressedWrong,
}: KeyboardProps) {
  // Track physically pressed keys to mirror on screen
  const [heldKeys, setHeldKeys] = useState<Set<string>>(new Set());

  useEffect(() => {
    function onDown(e: KeyboardEvent) {
      setHeldKeys((prev) => {
        const next = new Set(prev);
        next.add(e.key.toLowerCase());
        return next;
      });
    }
    function onUp(e: KeyboardEvent) {
      setHeldKeys((prev) => {
        const next = new Set(prev);
        next.delete(e.key.toLowerCase());
        return next;
      });
    }
    window.addEventListener("keydown", onDown);
    window.addEventListener("keyup", onUp);
    return () => {
      window.removeEventListener("keydown", onDown);
      window.removeEventListener("keyup", onUp);
    };
  }, []);

  // Normalize activeKey
  const baseActiveKey = SHIFT_MAP[activeKey] ?? activeKey.toLowerCase();
  const needsShift =
    activeKey !== activeKey.toLowerCase() ||
    Object.keys(SHIFT_MAP).includes(activeKey);

  const activeFinger = KEY_FINGERS[baseActiveKey];

  // Build set of lesson base keys
  const lessonBaseKeys = new Set<string>();
  for (const k of lessonKeys) {
    const base = SHIFT_MAP[k] ?? k.toLowerCase();
    if (base.length === 1) lessonBaseKeys.add(base);
  }

  return (
    <div className="flex flex-col items-center gap-1 select-none">
      {/* Finger guide showing which finger to use */}
      {activeKey && activeFinger !== undefined && (
        <div className="text-sm text-muted-foreground mb-1">
          Use your <span className="font-bold text-foreground">{FINGER_NAMES[activeFinger]}</span> finger
          {needsShift && (
            <span>
              {" "}+ <span className="font-bold text-foreground">{activeFinger <= 3 ? "R pinky" : "L pinky"}</span> on Shift
            </span>
          )}
        </div>
      )}

      {/* Hand diagram */}
      <div className="flex justify-center gap-8 mb-2">
        {/* Left hand */}
        <div className="flex gap-0.5 items-end">
          {["L pinky", "L ring", "L mid", "L index"].map((name, i) => {
            const isActive = activeFinger === i;
            const heights = [28, 36, 40, 34];
            return (
              <div
                key={name}
                className={cn(
                  "rounded-t-full transition-all duration-150",
                  "w-4",
                  isActive ? "bg-primary" : "bg-muted-foreground/20",
                )}
                style={{ height: heights[i] }}
                title={name}
              />
            );
          })}
          {/* thumb */}
          <div
            className={cn(
              "rounded-full w-5 h-5 ml-1 self-center transition-all duration-150",
              (activeKey === " " || (needsShift && activeFinger !== undefined && activeFinger > 3))
                ? "bg-primary" : "bg-muted-foreground/20",
            )}
            title="L thumb"
          />
        </div>

        {/* Right hand */}
        <div className="flex gap-0.5 items-end">
          {/* thumb */}
          <div
            className={cn(
              "rounded-full w-5 h-5 mr-1 self-center transition-all duration-150",
              (activeKey === " " || (needsShift && activeFinger !== undefined && activeFinger <= 3))
                ? "bg-primary" : "bg-muted-foreground/20",
            )}
            title="R thumb"
          />
          {["R index", "R mid", "R ring", "R pinky"].map((name, i) => {
            const fingerIdx = i + 4;
            const isActive = activeFinger === fingerIdx;
            const heights = [34, 40, 36, 28];
            return (
              <div
                key={name}
                className={cn(
                  "rounded-t-full transition-all duration-150",
                  "w-4",
                  isActive ? "bg-primary" : "bg-muted-foreground/20",
                )}
                style={{ height: heights[i] }}
                title={name}
              />
            );
          })}
        </div>
      </div>

      {/* Keyboard rows */}
      {ROWS.map((row, ri) => (
        <div
          key={ri}
          className="flex gap-1"
          style={{ paddingLeft: ri === 1 ? 20 : ri === 2 ? 36 : ri === 3 ? 52 : 0 }}
        >
          {row.map((key) => {
            const isLesson = lessonBaseKeys.has(key);
            const isActive = baseActiveKey === key;
            const isCorrect = pressedCorrect?.toLowerCase() === key;
            const isWrong = pressedWrong?.toLowerCase() === key;
            const isHeld = heldKeys.has(key);
            const finger = KEY_FINGERS[key] ?? 0;

            return (
              <div
                key={key}
                className={cn(
                  "flex flex-col items-center justify-center rounded-md border-2 border-b-4 text-xs font-mono font-bold transition-all duration-100",
                  "w-10 h-10 sm:w-11 sm:h-11",
                  FINGER_BORDER[finger],
                  !isLesson && "opacity-30 bg-muted text-muted-foreground",
                  isLesson && FINGER_BG[finger],
                  isLesson && "text-secondary-foreground",
                  isActive && "bg-primary text-primary-foreground animate-pulse scale-110 border-primary",
                  isHeld && !isActive && "scale-95 brightness-75",
                  isCorrect && "bg-green-500 text-white scale-95",
                  isWrong && "bg-destructive text-destructive-foreground scale-95",
                )}
              >
                <span>{key.toUpperCase()}</span>
              </div>
            );
          })}
        </div>
      ))}

      {/* Space bar row */}
      <div className="flex gap-1" style={{ paddingLeft: 52 }}>
        {needsShift && (
          <div
            className={cn(
              "flex items-center justify-center rounded-md border-2 border-b-4 text-xs font-mono font-bold w-20 h-10 sm:h-11",
              "bg-primary text-primary-foreground animate-pulse border-primary",
              heldKeys.has("shift") && "scale-95",
            )}
          >
            SHIFT
          </div>
        )}
        <div
          className={cn(
            "flex items-center justify-center rounded-md border-2 border-b-4 text-xs font-mono font-bold h-10 sm:h-11",
            needsShift ? "w-48" : "w-72",
            activeKey === " "
              ? "bg-primary text-primary-foreground animate-pulse border-primary"
              : "bg-secondary text-secondary-foreground",
            heldKeys.has(" ") && "scale-95 brightness-75",
          )}
        >
          SPACE
        </div>
        <div
          className={cn(
            "flex items-center justify-center rounded-md border-2 border-b-4 text-xs font-mono font-bold w-20 h-10 sm:h-11",
            activeKey === "\n"
              ? "bg-primary text-primary-foreground animate-pulse border-primary"
              : "bg-secondary text-secondary-foreground",
            heldKeys.has("enter") && "scale-95 brightness-75",
          )}
        >
          ENTER
        </div>
      </div>
    </div>
  );
}
